import { inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { registerErrorDispatcher } from '@core/api';

import { errorActions } from '../actions/error.actions';
import { ConfigEffects } from '../effects/config.effects';
import { HydrationEffects } from '../effects/hydration.effects';
import { InitEffects } from '../effects/init.effects';
import { LimitsEffects } from '../effects/limits.effects';
import { UserEffects } from '../effects/user.effects';
import * as fromConfig from '../reducers/config.reducer';
import * as fromError from '../reducers/error.reducer';
import { metaReducer } from '../reducers/hydration.reducer';
import * as fromInit from '../reducers/init.reducer';
import * as fromLimits from '../reducers/limits.reducer';
import * as fromOrganisation from '../reducers/organisation.reducer';
import * as fromPayment from '../reducers/payment.reducer';
import * as fromSubscription from '../reducers/subscription.reducer';
import * as fromTeams from '../reducers/teams.reducer';
import * as fromUser from '../reducers/user.reducer';

export interface AppState {
  [fromInit.featureKey]: fromInit.InitState;
  [fromUser.featureKey]: fromUser.UserState;
  [fromOrganisation.featureKey]: fromOrganisation.OrganisationState;
  [fromSubscription.featureKey]: fromSubscription.SubscriptionState;
  [fromLimits.featureKey]: fromLimits.LimitsState;
  [fromTeams.featureKey]: fromTeams.TeamsState;
  [fromPayment.featureKey]: fromPayment.PaymentState;
  [fromConfig.featureKey]: fromConfig.ConfigState;
  [fromError.featureKey]: fromError.ErrorState;
}

const reducers = {
  [fromInit.featureKey]: fromInit.reducer,
  [fromUser.featureKey]: fromUser.reducer,
  [fromOrganisation.featureKey]: fromOrganisation.reducer,
  [fromSubscription.featureKey]: fromSubscription.reducer,
  [fromLimits.featureKey]: fromLimits.reducer,
  [fromTeams.featureKey]: fromTeams.reducer,
  [fromPayment.featureKey]: fromPayment.reducer,
  [fromConfig.featureKey]: fromConfig.reducer,
  [fromError.featureKey]: fromError.reducer,
};

export const provideCoreStore = () => {
  return [
    provideStore(reducers, { metaReducers: [metaReducer] }),
    provideEffects(HydrationEffects, UserEffects, InitEffects, LimitsEffects, ConfigEffects),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode(), autoPause: true, trace: false, connectInZone: true }),
    provideAppInitializer(() => {
      const store = inject(Store);
      registerErrorDispatcher((error, level) => store.dispatch(errorActions.setError({ error, level })));
    }),
  ];
};
